import React from 'react';
import PropTypes from 'prop-types';
import {Map, List} from 'immutable';
import moment from 'moment';
import classnames from 'classnames';
import {axisBottom as d3AxisBottom} from 'd3-axis';
import {select as d3Select} from 'd3-selection';
import {extent as d3Extent, max as d3Max} from 'd3-array';
import {scaleLinear as d3ScaleLinear, scaleTime as d3ScaleTime} from 'd3-scale';
import {line as d3Line} from 'd3-shape';
import {timeMonth as d3TimeMonth, timeYear as d3TimeYear} from 'd3-time';
import {timeFormat as d3TimeFormat} from 'd3-time-format';
import {callTargetedAction, setUpStore} from 'client/framework';
import makeRef from 'lib/makeRef';
import lineChartActions from './LineChart.actions';
import LineChartStore from './LineChart.store';
import './line-chart.scss';

export default class LineChart extends React.Component {
  static propTypes = {
    data: PropTypes.instanceOf(List).isRequired,
    color: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
    tooltipFormat: PropTypes.string,
    storeName: PropTypes.string.isRequired,
    selectX: PropTypes.func,
    selectY: PropTypes.func,
    tooltipMessage: PropTypes.string,
    selectedInterval: PropTypes.string
  };

  static defaultProps = {
    height: 125,
    width: 1000,
    tooltipFormat: 'MMM D',
    labelFormat: 'MMM D',
    tooltipMessage: 'questions answered',
    selectX: (d) => d.date,
    selectY: (d) => {
      const count = parseFloat(d.count, 10);
      const percentAdjustedCount = Math.ceil(count + count * 0.1);
      return percentAdjustedCount;
    }
  };

  getStore() {
    return setUpStore(LineChartStore, this.props.storeName);
  }

  xScale = () => {
    return d3ScaleTime()
      .domain(d3Extent(this.props.data.toJS(), this.props.selectX))
      .range([0, this.props.width - 40]);
  };

  yScale = () => {
    return d3ScaleLinear()
      .domain([0, d3Max(this.props.data.toJS(), this.props.selectY)])
      .range([this.props.height, 0]);
  };

  generatePoints() {
    return this.props.data.map((point, index) => {
      const x = this.xScale();
      const y = this.yScale();
      return (
        <LineChartPoint
          key={index}
          point={point}
          x={x(point.get('date'))}
          y={y(point.get('count') || 0)}
          color={this.props.color}
          storeName={this.props.storeName}
        />
      );
    });
  }

  render() {
    const margins = new Map({
      bottom: 40,
      top: 5,
      right: 10,
      left: 10
    });

    const viewBoxTopBottomMargin = margins.get('top') * 2 + margins.get('bottom');
    const viewBoxLeftRightMargin = margins.get('left') + margins.get('right');

    return (
      <div id='line-graph__wrapper'>
        <LineChartTooltip
          tooltipFormat={this.props.tooltipFormat}
          tooltipMessage={this.props.tooltipMessage}
          selectedInterval={this.props.selectedInterval}
          store={this.getStore()}
        />
        <svg
          xmlns='http://www.w3.org/2000/svg'
          viewBox={`0 0 ${this.props.width + viewBoxLeftRightMargin} ${this.props.height +
            viewBoxTopBottomMargin}`}
          preserveAspectRatio='none'
        >
          <g className='graph'>
            <rect
              className='line-graph__background'
              height={`${this.props.height + margins.get('top')}px`}
              width='100%'
              rx='1em'
              ry='1em'
            />
            <LineChartLabels
              data={this.props.data}
              height={this.props.height}
              xScale={this.xScale()}
              margins={margins}
              xScale={this.xScale()}
            />
          </g>
          <g transform={`translate(${viewBoxLeftRightMargin * 1.5}, ${margins.get('top')})`}>
            <LineChartLine
              data={this.props.data}
              xScale={this.xScale()}
              yScale={this.yScale()}
              color={this.props.color}
            />
            {this.generatePoints()}
          </g>
        </svg>
      </div>
    );
  }
}

class LineChartTooltip extends React.Component {
  static propTypes = {
    tooltipFormat: PropTypes.string,
    tooltipMessage: PropTypes.string,
    selectedInterval: PropTypes.string,
    store: PropTypes.object
  };

  generateTooltipText() {
    const tooltipData = this.props.store.getTooltipData();
    const formattedDate = moment(tooltipData.get('date')).format(this.props.tooltipFormat);
    let tooltipText = `${tooltipData.get('count')} ${this.props.tooltipMessage} `;

    if (this.props.selectedInterval === 'month') {
      tooltipText += `in ${formattedDate}`;
    } else if (this.props.selectedInterval === 'week') {
      const endWeekDate = moment(tooltipData.get('date'))
        .add(6, 'days')
        .format(this.props.tooltipFormat);
      tooltipText += `from ${formattedDate} - ${endWeekDate}`;
    } else {
      tooltipText += `on ${formattedDate}`;
    }

    return tooltipText;
  }

  render() {
    const isTooltipVisible = this.props.store.isTooltipVisible();
    if (!isTooltipVisible) {
      return null;
    }

    const tooltipPosition = this.props.store.getTooltipPosition();

    return (
      <div
        className='tooltip-v2 line-graph__tooltip'
        style={{
          top: `${tooltipPosition.top}px`,
          left: `${tooltipPosition.left + tooltipPosition.width / 2}px`
        }}
      >
        {this.generateTooltipText()}
      </div>
    );
  }
}

class LineChartLabels extends React.Component {
  static propTypes = {
    data: PropTypes.instanceOf(List).isRequired,
    xScale: PropTypes.func,
    height: PropTypes.number,
    margins: PropTypes.instanceOf(Map)
  };

  componentDidMount() {
    d3Select(this.xAxisElement).call(this.xAxis());
  }

  componentDidUpdate() {
    d3Select(this.xAxisElement).call(this.xAxis());
  }

  makeRef = makeRef.bind(this);

  multiFormat = (date) => {
    let timeFormat = '%Y';
    if (d3TimeMonth(date) < date) {
      timeFormat = '%b %d';
    } else if (d3TimeYear(date) < date) {
      timeFormat = '%b';
    }

    return d3TimeFormat(timeFormat)(date);
  };

  xAxis = () => {
    return d3AxisBottom()
      .scale(this.props.xScale)
      .ticks(Math.min(this.props.data.size, 12))
      .tickFormat(this.multiFormat);
  };

  generatePointLines() {
    return this.props.data.map((data, index) => {
      return (
        <g key={index}>
          <line
            x1={this.props.xScale(data.get('date')) + this.props.margins.get('left')}
            x2={this.props.xScale(data.get('date')) + this.props.margins.get('left')}
            y1='0'
            y2={`${this.props.height + this.props.margins.get('top')}px`}
            strokeWidth='1'
            className='line-graph__label-line'
          />
        </g>
      );
    });
  }

  render() {
    if (this.props.data.isEmpty()) {
      return (
        <text textAnchor='middle' x='50%' y='40%'>
          There's no chart data for the filters you selected.
        </text>
      );
    }

    const margin = this.props.margins.get('left') + this.props.margins.get('right');
    return (
      <g transform={`translate(${margin}, 0)`} className='line-graph__labels'>
        {this.generatePointLines()}
        <g
          className='line-graph__label'
          ref={this.makeRef}
          data-ref-name='xAxisElement'
          style={{
            transform: `translate(${this.props.margins.get('left')}px, ${this.props.height + 20}px)`
          }}
        />
      </g>
    );
  }
}

class LineChartLine extends React.Component {
  static propTypes = {
    data: PropTypes.instanceOf(List).isRequired,
    xScale: PropTypes.func,
    yScale: PropTypes.func,
    color: PropTypes.string
  };

  generateLineData() {
    const mutableData = this.props.data.toJS();

    const line = d3Line()
      .x((d) => this.props.xScale(d.date))
      .y((d) => this.props.yScale(d.count || 0));

    return line(mutableData);
  }

  render() {
    const lineClasses = classnames('line-graph__data-line', {
      'line-graph__data-line--red': this.props.color === 'red',
      'line-graph__data-line--purple': this.props.color === 'purple'
    });

    return <path className={lineClasses} d={this.generateLineData()} />;
  }
}

class LineChartPoint extends React.Component {
  static propTypes = {
    point: PropTypes.instanceOf(Map).isRequired,
    x: PropTypes.number,
    y: PropTypes.number,
    color: PropTypes.string,
    storeName: PropTypes.string
  };

  showTooltip(e) {
    const pointPosition = e.target.getBoundingClientRect();
    const tooltipData = new Map({
      data: this.props.point,
      position: pointPosition
    });
    callTargetedAction({
      name: lineChartActions.SET_TOOLTIP_DATA,
      payload: tooltipData,
      targetStore: this.props.storeName
    });
    callTargetedAction({
      name: lineChartActions.TOGGLE_TOOLTIP,
      targetStore: this.props.storeName,
      payload: true
    });
  }

  hideTooltip() {
    callTargetedAction({
      name: lineChartActions.CLEAR_TOOLTIP_DATA,
      targetStore: this.props.storeName
    });
    callTargetedAction({
      name: lineChartActions.TOGGLE_TOOLTIP,
      targetStore: this.props.storeName,
      payload: false
    });
  }

  render() {
    const pointClasses = classnames('line-graph__data-point', {
      'line-graph__data-point--red': this.props.color === 'red',
      'line-graph__data-point--purple': this.props.color === 'purple'
    });

    return (
      <g>
        <circle
          className={pointClasses}
          cx={this.props.x}
          cy={this.props.y}
          r='5'
          strokeWidth='2px'
          onMouseEnter={(e) => {
            this.showTooltip(e);
          }}
          onMouseLeave={(e) => {
            this.hideTooltip(e);
          }}
        />
      </g>
    );
  }
}
